import { CurrencyTextField, DateInput, NumberInput, TextInput } from '@core/components/Inputs';
import { SelectInput } from '@core/components/Select';
import {
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@material-ui/core';
import { parametrosReajusteContratoApi } from 'app/services/api/parametrosReajusteContratoApi';
import { ONLY_NUMBER_REGEX } from 'app/utils/patternUtil';
import { useFormikContext } from 'formik';
import moment from 'moment';
import React, { useEffect } from 'react';

const ReajusteAutomatico = ({ values, handleChange, handleBlur, errors, submitCount, touched }) => {
  const _errors = {
    periodicidadeMeses:
      (submitCount > 0 || touched?.reajusteAutomatico?.periodicidadeMeses) &&
      errors?.reajusteAutomatico?.periodicidadeMeses,
    reajustarPor:
      (submitCount > 0 || touched?.reajusteAutomatico?.reajustarPor) && errors?.reajusteAutomatico?.reajustarPor,
    indiceMonetario:
      (submitCount > 0 || touched?.reajusteAutomatico?.indiceMonetario) && errors?.reajusteAutomatico?.indiceMonetario,
    valorReajuste:
      (submitCount > 0 || touched?.reajusteAutomatico?.valorReajuste) && errors?.reajusteAutomatico?.valorReajuste,
    mesBaseReajuste:
      (submitCount > 0 || touched?.reajusteAutomatico?.mesBaseReajuste) && errors?.reajusteAutomatico?.mesBaseReajuste,
  };

  const msgCalculoIniciado = () => {
    return values?.bloquearEdicao
      ? 'O cálculo para reajuste automático já foi iniciado, só será possível alterar essa informação para o próximo reajuste.'
      : '';
  };

  useEffect(() => {
    const getParametrosDefautls = async () => {
      const resp = await parametrosReajusteContratoApi().getParametrosReajusteContratoEmpresa();
      const value = {
        aplicarReajusteAutomatico: resp.data.aplicarReajusteAutomatico || false,
        reajustarPor: resp.data.reajustarPor || 'INDICE',
        indiceMonetario: resp.data.indiceMonetario || '',
        valorReajuste: resp.data.valorReajuste || 0,
        periodicidadeMeses: resp.data.periodicidadeMeses || 12,
        mesBaseReajuste: moment(),
      };
      const event = {
        target: {
          id: 'reajusteAutomatico',
          value,
        },
      };
      handleChange(event);
    };
    if (!values.id) {
      getParametrosDefautls();
    }
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const naoExibirZero = (value) => {
    if (value === 0) {
      value = null;
    }
    return value;
  };

  const formikContext = useFormikContext();

  const { setErrors, setTouched } = formikContext;
  useEffect(() => {
    setTouched({});
    setErrors({});
  }, [values.reajustarPor, setErrors, setTouched]);

  const handleChangeReajusteContratos = (event) => {
    handleChange(event);
    const resetEvent = {
      target: {
        id: 'reajusteAutomatico.valorReajuste',
        value: 0,
      },
    };
    handleChange(resetEvent);
  };

  return (
    <>
      <Grid container spacing={3} className="mt-16 mb-16">
        <Grid item xs={12}>
          <Tooltip title={msgCalculoIniciado()} placement="top-start">
            <span>
              <FormControlLabel
                label="Reajuste automático"
                control={
                  <Switch
                    disabled={values?.bloquearEdicao}
                    id="reajusteAutomatico.aplicarReajusteAutomatico"
                    name="reajusteAutomatico.aplicarReajusteAutomatico"
                    value={values?.aplicarReajusteAutomatico}
                    checked={values?.aplicarReajusteAutomatico}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="small"
                    color="primary"
                  />
                }
              />
            </span>
          </Tooltip>
        </Grid>
        {values?.aplicarReajusteAutomatico && (
          <>
            <Grid item xs={3}>
              <Tooltip title={msgCalculoIniciado()} placement="top-start">
                <span>
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <InputLabel id="reajusteAutomatico.reajustarPor" required>
                      Reajustar contrato por
                    </InputLabel>
                    <Select
                      required
                      disabled={values?.bloquearEdicao}
                      id="reajusteAutomatico.reajustarPor"
                      name="reajusteAutomatico.reajustarPor"
                      label="Reajustar contrato por"
                      labelId="reajustar-por"
                      value={values?.reajustarPor}
                      onChange={handleChangeReajusteContratos}
                      onBlur={handleBlur}
                      error={_errors.reajustarPor}
                      helperText={_errors.reajustarPor}
                      touched={!!_errors.reajustarPor}
                    >
                      <MenuItem value="INDICE">Índice monetário</MenuItem>
                      <MenuItem value="PERCENTUAL">Percentual (%)</MenuItem>
                      <MenuItem value="VALOR">Valor (R$)</MenuItem>
                    </Select>
                  </FormControl>
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={3}>
              <Tooltip
                title={
                  values?.bloquearEdicao
                    ? msgCalculoIniciado()
                    : 'Informe o índice monetário, percentual ou valor desejado para reajuste do contrato automaticamente.'
                }
                placement="top-start"
              >
                <span>
                  {values?.reajustarPor === 'INDICE' && (
                    <SelectInput
                      disabled={values?.bloquearEdicao}
                      label="Índice monetário"
                      id="reajusteAutomatico.indiceMonetario"
                      name="reajusteAutomatico.indiceMonetario"
                      required
                      error={_errors.indiceMonetario}
                      touched={!!_errors.indiceMonetario}
                      helperText={_errors.indiceMonetario}
                      onChange={handleChange}
                      value={values?.indiceMonetario}
                      options={[
                        { title: 'IGP-M', id: 'IGP_M' },
                        { title: 'IGP-DI', id: 'IGP_DI' },
                        { title: 'TR', id: 'TR' },
                        { title: 'INPC', id: 'INPC' },
                        { title: 'IPCA', id: 'IPCA' },
                        { title: 'DEPRE', id: 'DEPRE' },
                        { title: 'TJSP', id: 'TJSP' },
                        { title: 'JAM', id: 'JAM' },
                        { title: 'IPC-M', id: 'IPC_M' },
                        { title: 'TJRJ / UFIR-RJ', id: 'TJRJ' },
                        { title: 'TJRO', id: 'TJRO' },
                        { title: 'TJPE', id: 'TJPE' },
                        { title: 'TJES', id: 'TJES' },
                      ]}
                    />
                  )}
                  {values?.reajustarPor === 'PERCENTUAL' && (
                    <NumberInput
                      required
                      label="Percentual (%)"
                      id="reajusteAutomatico.valorReajuste"
                      name="reajusteAutomatico.valorReajuste"
                      decimalScale={2}
                      decimalSeparator=","
                      allowNegative={false}
                      isAllowed={(values) => {
                        return !values.floatValue || values.floatValue <= 100;
                      }}
                      value={naoExibirZero(values?.valorReajuste)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={_errors.valorReajuste}
                      touched={!!_errors.valorReajuste}
                      helperText={_errors.valorReajuste}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  )}
                  {values?.reajustarPor === 'VALOR' && (
                    <CurrencyTextField
                      required
                      id="reajusteAutomatico.valorReajuste"
                      name="valorReajuste"
                      value={values?.valorReajuste}
                      currencySymbol="R$"
                      outputFormat="number"
                      decimalCharacter=","
                      digitGroupSeparator="."
                      maximumValue="1000000000"
                      minimumValue="0"
                      decimalPlaces={2}
                      decimalPlacesShownOnBlur={2}
                      decimalPlacesShownOnFocus={2}
                      error={_errors.valorReajuste}
                      touched={!!_errors.valorReajuste}
                      helperText={_errors.valorReajuste}
                      onChange={(e, value) => {
                        const event = {
                          target: {
                            id: e.target.id,
                            value: e.target.value === '' ? '' : value,
                          },
                        };
                        handleChange(event);
                      }}
                      onBlur={handleBlur}
                      fullWidth
                      variant="outlined"
                      label="Valor (R$)"
                    />
                  )}
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={3}>
              <Tooltip
                title={
                  values?.bloquearEdicao
                    ? msgCalculoIniciado()
                    : 'Mês de prestação de serviço que deve ser  considerado como base para cálculo do reajuste automático do contrato. A partir do mês informado, será calculado o mês de prestação de serviço que deverá ter reajuste e ele será feito automaticamente conforme o tipo da cobrança e a periodicidade.'
                }
                placement="top-start"
              >
                <span>
                  <DateInput
                    required
                    disabled={values?.bloquearEdicao}
                    fullWidth
                    onBlur={handleBlur}
                    id="reajusteAutomatico.mesBaseReajuste"
                    name="reajusteAutomatico.mesBaseReajuste"
                    label="Mês base para reajuste"
                    value={values?.mesBaseReajuste}
                    onChange={handleChange}
                    touched={_errors.mesBaseReajuste}
                    error={_errors.mesBaseReajuste}
                    helperText={_errors.mesBaseReajuste}
                    format="MM/YYYY"
                  />
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={3}>
              <Tooltip
                title={
                  values?.bloquearEdicao
                    ? msgCalculoIniciado()
                    : 'Quantidade de meses a ser considerado para reajustar o valor do contrato automaticamente.'
                }
                placement="top-start"
              >
                <span>
                  <TextInput
                    disabled={values?.bloquearEdicao}
                    id="reajusteAutomatico.periodicidadeMeses"
                    name="reajusteAutomatico.periodicidadeMeses"
                    onChange={handleChange}
                    label="Periodicidade do reajuste em meses"
                    fullWidth
                    variant="outlined"
                    value={values?.periodicidadeMeses}
                    maxLength={3}
                    pattern={ONLY_NUMBER_REGEX}
                    required
                    error={_errors.periodicidadeMeses}
                    helperText={_errors.periodicidadeMeses}
                    touched={!!_errors.periodicidadeMeses}
                  />
                </span>
              </Tooltip>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default ReajusteAutomatico;
